import getStore from "@utils/redux/store";
import { getURLParams } from "@utils/urlParams";
import { setUserProperties } from "@utils/redux/slices/userData";
import { setFeatureRolloutType } from "@utils/redux/slices/persistentBrowserFlags";
import type { UserDataState } from "@utils/redux/slices/userData";
import { appConfig } from "src/config";
import { capitalizeFirstLetter } from "src/primitive/string";
import { getMedFlagOverride } from "./userSegment/features";

function getTrafficPercentageParam(key: string) {
  const param = new URLSearchParams(window.location.search).get(key);
  if (param) {
    return parseFloat(param) / 100;
  }
  return undefined;
}

// Types
export enum RolloutUserType {
  CONTROL = "CONTROL",
  VARIATION = "VARIATION",
  HOLDBACK = "HOLDBACK",
  FORCED_ENABLED = "FORCED_ENABLED",
  FORCED_DISABLED = "FORCED_DISABLED",
}

export interface RolloutConfig {
  featureName: string;
  enabled: boolean;
  trafficSplit: number;
  holdbackTraffic: number;
  autoAssign?: boolean;
}

export interface RolloutState {
  rolloutType: RolloutUserType | null;
}

// Type for userData rollout storage
export interface UserDataRollouts {
  [featureName: string]: RolloutUserType | null;
}

// Base Rollout Class
export class FeatureRollout {
  private config: RolloutConfig;

  constructor(config: RolloutConfig) {
    this.config = {
      ...config,
      trafficSplit:
        getTrafficPercentageParam(
          `${config.featureName}TrafficSplitPercentage`
        ) ?? config.trafficSplit,
      holdbackTraffic:
        getTrafficPercentageParam(`${config.featureName}HoldbackPercentage`) ??
        config.holdbackTraffic,
      autoAssign: config.autoAssign ?? true,
      enabled:
        getMedFlagOverride(`${config.featureName}RolloutEnabled`) ??
        config.enabled,
    };
  }

  static create(config: RolloutConfig): FeatureRollout {
    return new FeatureRollout({
      ...config,
    });
  }

  private getForcedParam(): string | null {
    const forcedParamName = `enable${capitalizeFirstLetter(
      this.config.featureName
    )}`;
    return getURLParams()?.[forcedParamName] ?? null;
  }

  private getCurrentRolloutType(): RolloutUserType | null {
    const store = getStore();
    const { persistentBrowserFlags, userData } = store.getState();
    return (
      persistentBrowserFlags.featureRolloutTypes?.[this.config.featureName]
        ?.rolloutType ||
      (userData.featureRolloutTypes as UserDataRollouts)?.[
        this.config.featureName
      ] ||
      null
    );
  }

  private persistRolloutType(type: RolloutUserType): void {
    const store = getStore();
    const { persistentBrowserFlags, userData } = store.getState();

    // Check and update persistentBrowserFlags if different
    const existingPersistentType =
      persistentBrowserFlags.featureRolloutTypes?.[this.config.featureName]
        ?.rolloutType;

    if (type !== existingPersistentType) {
      store.dispatch(
        setFeatureRolloutType({
          feature: this.config.featureName,
          rolloutType: type,
        })
      );
    }

    // Check and update userData if different
    const existingUserDataType = (
      userData.featureRolloutTypes as UserDataRollouts
    )?.[this.config.featureName];

    if (type !== existingUserDataType) {
      const currentRollouts = (userData.featureRolloutTypes ||
        {}) as UserDataRollouts;

      store.dispatch(
        setUserProperties({
          featureRolloutTypes: {
            ...currentRollouts,
            [this.config.featureName]: type,
          },
        } as Partial<UserDataState>)
      );
    }
  }

  private determineRolloutType(): RolloutUserType {
    // Check for forced parameter override
    const forcedParam = this.getForcedParam();
    if (forcedParam === "true") {
      return RolloutUserType.FORCED_ENABLED;
    }
    if (forcedParam === "false") {
      return RolloutUserType.FORCED_DISABLED;
    }

    // If feature is not enabled, return FORCED_DISABLED
    if (!this.config.enabled) {
      return RolloutUserType.FORCED_DISABLED;
    }

    // Check for existing assignment
    const currentType = this.getCurrentRolloutType();
    if (currentType) {
      // When at 100% rollout, override cached state to serve feature to everyone
      if (
        this.config.holdbackTraffic === 0 &&
        this.config.trafficSplit === 1.0
      ) {
        return RolloutUserType.FORCED_ENABLED;
      }
      return currentType;
    }

    // Check holdback first
    if (Math.random() < this.config.holdbackTraffic) {
      return RolloutUserType.HOLDBACK;
    }

    // Then check traffic split
    if (Math.random() < this.config.trafficSplit) {
      return RolloutUserType.VARIATION;
    }

    // Default to control
    return RolloutUserType.CONTROL;
  }

  private setRolloutType(): RolloutUserType {
    const rolloutType = this.determineRolloutType();
    this.persistRolloutType(rolloutType);
    return rolloutType;
  }

  getRolloutStatus(): RolloutUserType {
    if (this.config.autoAssign) {
      return this.setRolloutType();
    }
    return this.getCurrentRolloutType();
  }

  isInPercentRollout(): boolean {
    const status = this.getRolloutStatus();
    return [RolloutUserType.FORCED_ENABLED, RolloutUserType.VARIATION].includes(
      status
    );
  }

  shouldEnableFeature(): boolean {
    return this.config.enabled && this.isInPercentRollout();
  }

  isRolloutEnabled(): boolean {
    return this.config.enabled;
  }
}

const LIRAGLUTIDE_ROLLOUT: RolloutConfig = {
  featureName: "liraglutideMedPlans",
  enabled: appConfig.LIRAGLUTIDE_MED_PLANS_ENABLED,
  trafficSplit: appConfig.LIRAGLUTIDE_MED_PLANS_TRAFFIC_SPLIT,
  holdbackTraffic: appConfig.LIRAGLUTIDE_MED_PLANS_HOLDBACK_TRAFFIC,
  autoAssign: true,
};

export const getLiraglutideRollout = () =>
  FeatureRollout.create(LIRAGLUTIDE_ROLLOUT);

const HRT_PHASE_2_FOLLOW_UP_ROLLOUT: RolloutConfig = {
  featureName: "hrtPhase2FollowUp",
  enabled: appConfig.HRT_PHASE_2_FOLLOW_UP_ENABLED,
  trafficSplit: appConfig.HRT_PHASE_2_FOLLOW_UP_TRAFFIC_SPLIT,
  holdbackTraffic: 0,
  autoAssign: true,
};

export const getHRTPhase2FollowUpRollout = () =>
  FeatureRollout.create(HRT_PHASE_2_FOLLOW_UP_ROLLOUT);
